import React, { useEffect, FC, useState, useMemo } from "react";
import { Modal, ModalBody, Form, Row, Alert, Col, Button } from "reactstrap";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import { Icon, TooltipComponent } from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import FormMultiSelect from "../form-components/MultiSelectComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormTextArea from "../form-components/TextAreaComponent";
import { Popover } from "antd";
import deliveryAddressApis from "../../api/master/deliveryAddress";
import { nanoid } from "nanoid";
import supplierApis from "../../api/master/supplier";
import brandMasterApis from "../../api/master/brandMaster";
interface SelectOptions {
  label: string;
  value: any;
}


type Cess = {
  cessName: string;
  cessType?: string;
  cessPercentage?: string;
  cessAmount?: string;
};
interface TaxConfiguration {
  taxType: string;
  gstType?: string; //IGST or CGST+SGST
  cess?: Cess[];
}
const TAX_SLAP_DROPDOWN = [
  {
    label: "0%",
    value: "0",
  },
  {
    label: "5%",
    value: "1",
  },
  {
    label: "12%",
    value: "2",
  },
  {
    label: "18%",
    value: "3",
  },
  {
    label: "28%",
    value: "4",
  },
];
const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  taxConfiguration,
  needByDateType,
  deliveryAddressType,
  materialCategoryOptions,
  materialsList,
  siteId,
}) => {
  const { watch: watchFormContext, getValues: getParentValues } =
    useFormContext();
  const quotationId = watchFormContext("quotationId");
  const convertedFrom = watchFormContext("convertedFrom");
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [makeOptions, setMakeOptions] = useState<SelectOptions[]>([]);

  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);
  const [dmsFile, setDmsFile] = useState<any>(null);

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    watch: watchLocal,
    formState: { errors },
    setValue,
  } = methods;

  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  const files = watchLocal("attachment");
  const materialNameLocal = watchLocal("materialName");

  // Generate dynamic tax options including rate contract tax percentage
  const taxDropdownOptions = useMemo(() => {
    const baseOptions = [...TAX_SLAP_DROPDOWN];

    // If converting from rate contract, check if tax percentage exists in formData.item
    if (convertedFrom === "RC" && formData?.item) {
      const item = formData.item;

      // Get the tax percentage from rate contract
      const originalTaxPercentage =
        item.originalTaxPercentage ||
        (item.igstPercentage > 0
          ? item.igstPercentage
          : Number(item.sgstPercentage || 0) +
          Number(item.cgstPercentage || 0));

      if (originalTaxPercentage > 0) {
        // Check if this tax percentage already exists in dropdown
        const taxPercentageStr = originalTaxPercentage.toString();
        const exists = baseOptions.some(
          (opt: any) =>
            opt.label === `${taxPercentageStr}%` ||
            opt.label.includes(`${taxPercentageStr}%`) ||
            (opt.taxRate && opt.taxRate == originalTaxPercentage)
        );

        if (!exists) {
          // Add rate contract tax percentage to dropdown
          const label =
            originalTaxPercentage % 1 === 0
              ? `${originalTaxPercentage}%`
              : `${originalTaxPercentage.toFixed(2)}%`;

          // Use a custom value that can be identified later
          baseOptions.push({
            label: label,
            value: `rc_${originalTaxPercentage}`,
            taxRate: originalTaxPercentage,
            isFromRateContract: true,
          } as any);
        }
      }
    }

    return baseOptions;
  }, [convertedFrom, formData?.item]);

  // For RC-converted items, ensure the category option is available even if
  // fetchSupplierCategory didn't return it for this site
  const materialCategoryOptionsWithRC = useMemo(() => {
    if (
      convertedFrom === "RC" &&
      formData?.item?.rateContractLineId &&
      formData?.item?.materialCategory &&
      formData?.item?.categoryName
    ) {
      const catId = String(formData.item.materialCategory);
      const exists = materialCategoryOptions.some(
        (opt: any) => opt.value == catId
      );
      if (!exists) {
        return [
          ...materialCategoryOptions,
          { label: formData.item.categoryName, value: catId },
        ];
      }
    }
    return materialCategoryOptions;
  }, [materialCategoryOptions, convertedFrom, formData?.item]);

  useEffect(() => {
    if (files && files[0]?.size && files[0].size > 2097152) {
      setValue("attachment", null);
      setAlertInfo({
        type: "error",
        message: "File size should be less than 2 MB",
        errors: [],
      });
    }
  }, [files]);

  const deliveryAddressId = watchLocal("deliveryAddressId");
  const siteIdWatch = watchLocal("siteId");
  const resolvedSiteId = siteId ?? siteIdWatch ?? getParentValues("siteId");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const fetchDeliveryAddress = async () => {
    try {
      const sid = resolvedSiteId ?? siteId;
      if (sid) {
        const { data, status, message } =
          await deliveryAddressApis.fetchAddressBySite(sid);
        if (status) {
          let deliveryAddressOptions = data.map((item: any) => ({
            label: item.deliveryLocationName,
            value: item.id.toString(),
          }));
          setDeliveryAddressOptions(deliveryAddressOptions);
          setDeliveryAddressList(data);
        } else {
          console.log(message);
        }
      }
    } catch (error) {
      console.log("Error fetching delivery addresses:", error);
    }
  };

  useEffect(() => {
    fetchDeliveryAddress();
  }, [siteId, resolvedSiteId]);

  const fetchMakeOptions = async (materialId: any) => {
    if (!materialId) {
      setMakeOptions([]);
      return;
    }
    try {
      const response = await brandMasterApis.fetchBrandById(materialId);
      if (response?.status && response?.data?.brands) {
        const options = response.data.brands
          .filter((brand: any) => brand.status?.toUpperCase() === "ACTIVE")
          .map((brand: any) => ({
            label: brand.make,
            value: brand.id,
          }));
        setMakeOptions(options);
      } else {
        setMakeOptions([]);
      }
    } catch (error) {
      console.log("Failed to fetch make options", error);
      setMakeOptions([]);
    }
  };

  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const sid = resolvedSiteId ?? siteId;
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId,
        sid
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        if (
          (formData.action === "add" || formData.index === null) &&
          needByDateType == "Order Level" &&
          deliveryAddressType == "Order Level" &&
          true
        ) {
          if (Array.isArray(materialOption)) {
            const filteredOptions = materialOption.filter(
              (option) =>
                !materialsList || !Array.isArray(materialsList) || !materialsList.some((row: any) => row?.materialName?.toString() === option.value)
            );
            setMaterialOptions(filteredOptions);
          }
        } else {
          setMaterialOptions(materialOption);
        }
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);
  useEffect(() => {
    if (modal && formData.item) {
      const {
        materialCategory,
        materialName,
        hsnCode,
        quantity,
        unitPrice,
        cgst,
        sgst,
        igst,
        deliveryAddressId,
        description,
        needByDate,
        file,
        dms,
      } = formData.item;
      // PR conversion sends uomId under `uom` key; fall back gracefully
      const uomId = formData.item?.uomId ?? formData.item?.uom;
      console.log("MaterialModal formData.item:", formData.item);
      console.log("Tax values - cgst:", cgst, "sgst:", sgst, "igst:", igst);
      setValue("materialCategory", materialCategory);
      const isRC = convertedFrom === "RC" && formData.item?.rateContractLineId;
      (async () => {
        const _materialData = await fetchMaterials(
          materialCategory,
          materialName
        );
        // After fetchMaterials completes, ensure RC material option exists
        if (isRC && formData.item?.materialLabel && materialName) {
          setMaterialOptions((prev) => {
            if (prev.some((o) => String(o.value) === String(materialName))) return prev;
            return [
              ...prev,
              { label: formData.item.materialLabel, value: String(materialName) },
            ];
          });
        }
        setValue("materialName", String(materialName));

        await Promise.all([
          fetchUom(materialName, _materialData, uomId),
          fetchMakeOptions(materialName),
        ]);
        if (formData.item?.makeInfo?.length > 0) {
          setValue("make", formData.item.makeInfo.map((m: any) => Number(m.makeId)));
        }
        // Ensure source UOM option exists (RC, PR, Quotation conversions)
        const sourceUomLabel =
          formData.item?.uomLabel ||
          formData.item?.uomName ||
          formData.item?.uom;
        if (sourceUomLabel && uomId) {
          setUomOptions((prev) => {
            if (prev.some((o) => String(o.value) === String(uomId))) return prev;
            return [
              ...prev,
              { label: sourceUomLabel, value: String(uomId) },
            ];
          });
        }
        setValue("uomId", String(uomId));
      })();
      setValue("hsnCode", hsnCode);
      setValue("quantity", quantity);
      setValue("unitPrice", Number(unitPrice));
      // Set tax values - convert to string if needed, use undefined if null/undefined
      setValue("cgst", cgst != null ? String(cgst) : undefined);
      setValue("sgst", sgst != null ? String(sgst) : undefined);
      setValue("igst", igst != null ? String(igst) : undefined);
      setValue("description", description);
      setValue("attachment", file);
      if (formData.index != null) {
        setValue("deliveryAddressId", deliveryAddressId);
        setValue("needByDate", needByDate);
      }
      if (dms?.length > 0) {
        setDmsFile(dms[0]);
      }
    }
  }, [modal, formData?.item, formData?.index]);

  const submitForm = (data: any) => {
    const {
      materialCategory,
      materialName,
      hsnCode,
      quantity,
      unitPrice,
      igst,
      cgst,
      description,
      uomId,
      make,
      needByDate,
      attachment,
      deliveryAddressId,
    } = data;
    // Normalize numeric inputs to avoid NaN issues
    const qty = Number(quantity) || 0;
    const pricePerUnit = Number(unitPrice) || 0;
    let _cess: Cess[] = [];
    let cessTotal = 0;
    taxConfiguration?.cess?.forEach((cessItem: any, index: any) => {
      if (!cessItem.cessName) return;
      const rawCessPercentage = data[cessItem.cessName];
      const cessPercentage = Number(rawCessPercentage) || 0;
      const cessAmount = (cessPercentage * qty * pricePerUnit) / 100;
      cessTotal += cessAmount;
      _cess.push({
        cessName: cessItem.cessName,
        cessType: cessItem.cessType,
        cessPercentage: cessPercentage.toString(),
        cessAmount: cessAmount?.toString(),
      });
    });
    // Ensure amount is always a valid number
    const calculatedAmount = qty * pricePerUnit;
    const safeAmount =
      isNaN(calculatedAmount) || !isFinite(calculatedAmount)
        ? 0
        : calculatedAmount;

    const item = {
      materialName,
      hsnCode,
      quantity: qty,
      unitPrice: pricePerUnit,
      amount: safeAmount,
      description,
      igst: 0,
      igstPercentage: 0,
      igstAmount: 0,
      cgst: 0,
      cgstPercentage: 0,
      cgstAmount: 0,
      sgst: 0,
      sgstPercentage: 0,
      sgstAmount: 0,
      lineTotal: 0,
    };
    item.igst = 0;
    item.igstAmount = 0;
    item.igstPercentage = 0;
    item.cgst = 0;
    item.cgstAmount = 0;
    item.cgstPercentage = 0;
    item.sgst = 0;
    item.sgstAmount = 0;
    item.sgstPercentage = 0;
    let selectedTax;
    // Helper function to extract tax rate from dropdown value (handles rc_ prefix for rate contract)
    const getTaxRate = (taxValue: any, dropdown: any[]) => {
      if (!taxValue) return null;
      const taxStr = taxValue.toString();

      // Check if it's a rate contract custom value (rc_6 format)
      if (taxStr.startsWith("rc_")) {
        const rate = parseFloat(taxStr.replace("rc_", ""));
        return isNaN(rate) ? null : rate;
      }

      // Find in dropdown
      const found = dropdown.find((item) => item.value === taxStr);
      if (found) {
        // Extract from label or use taxRate if available
        if (found.taxRate) return found.taxRate;
        const rate = parseFloat(found.label.replace("%", ""));
        return isNaN(rate) ? null : rate;
      }

      return null;
    };

    switch (taxConfiguration?.taxType) {
      case "IGST":
        item.igst = igst;
        const igstRate = getTaxRate(igst, taxDropdownOptions);
        if (igstRate !== null && !isNaN(igstRate) && isFinite(igstRate)) {
          item.igstPercentage = igstRate; // Full percentage for IGST
          const calculatedIgstAmount = (igstRate * safeAmount) / 100;
          item.igstAmount =
            isNaN(calculatedIgstAmount) || !isFinite(calculatedIgstAmount)
              ? 0
              : calculatedIgstAmount;
        } else {
          item.igstPercentage = 0;
          item.igstAmount = 0;
        }
        break;
      case "CGST":
        item.cgst = cgst;
        item.sgst = cgst;
        const cgstRate = getTaxRate(cgst, taxDropdownOptions);
        if (cgstRate !== null && !isNaN(cgstRate) && isFinite(cgstRate)) {
          // Split by 2 for CGST+SGST
          item.cgstPercentage = cgstRate / 2;
          item.sgstPercentage = cgstRate / 2;
          const calculatedCgstAmount = (item.cgstPercentage * safeAmount) / 100;
          const calculatedSgstAmount = (item.sgstPercentage * safeAmount) / 100;
          item.cgstAmount =
            isNaN(calculatedCgstAmount) || !isFinite(calculatedCgstAmount)
              ? 0
              : calculatedCgstAmount;
          item.sgstAmount =
            isNaN(calculatedSgstAmount) || !isFinite(calculatedSgstAmount)
              ? 0
              : calculatedSgstAmount;
        } else {
          item.cgstPercentage = 0;
          item.sgstPercentage = 0;
          item.cgstAmount = 0;
          item.sgstAmount = 0;
        }
        break;
      default:
        break;
    }

    if (igst) {
      item.igst = igst;
      const igstRateValue = getTaxRate(igst, taxDropdownOptions);
      if (
        igstRateValue !== null &&
        !isNaN(igstRateValue) &&
        isFinite(igstRateValue)
      ) {
        item.igstPercentage = igstRateValue; // Full percentage for IGST
        const calculatedIgstAmount = (igstRateValue * safeAmount) / 100;
        item.igstAmount =
          isNaN(calculatedIgstAmount) || !isFinite(calculatedIgstAmount)
            ? 0
            : calculatedIgstAmount;
      } else {
        item.igstPercentage = 0;
        item.igstAmount = 0;
      }
    }
    if (cgst) {
      item.cgst = cgst;
      item.sgst = cgst;
      const cgstRateValue = getTaxRate(cgst, taxDropdownOptions);
      if (
        cgstRateValue !== null &&
        !isNaN(cgstRateValue) &&
        isFinite(cgstRateValue)
      ) {
        // Split by 2 for CGST+SGST
        item.cgstPercentage = cgstRateValue / 2;
        item.sgstPercentage = cgstRateValue / 2;
        const calculatedCgstAmount = (item.cgstPercentage * safeAmount) / 100;
        const calculatedSgstAmount = (item.sgstPercentage * safeAmount) / 100;
        item.cgstAmount =
          isNaN(calculatedCgstAmount) || !isFinite(calculatedCgstAmount)
            ? 0
            : calculatedCgstAmount;
        item.sgstAmount =
          isNaN(calculatedSgstAmount) || !isFinite(calculatedSgstAmount)
            ? 0
            : calculatedSgstAmount;
      } else {
        item.cgstPercentage = 0;
        item.sgstPercentage = 0;
        item.cgstAmount = 0;
        item.sgstAmount = 0;
      }
    }

    // Safely compute line total, guarding against NaN in any component
    const safeAmountForTotal =
      isNaN(Number(item.amount)) || !isFinite(Number(item.amount))
        ? 0
        : Number(item.amount);
    const safeIgstAmount =
      isNaN(Number(item.igstAmount)) || !isFinite(Number(item.igstAmount))
        ? 0
        : Number(item.igstAmount);
    const safeCgstAmount =
      isNaN(Number(item.cgstAmount)) || !isFinite(Number(item.cgstAmount))
        ? 0
        : Number(item.cgstAmount);
    const safeSgstAmount =
      isNaN(Number(item.sgstAmount)) || !isFinite(Number(item.sgstAmount))
        ? 0
        : Number(item.sgstAmount);
    const safeCessTotal =
      isNaN(Number(cessTotal)) || !isFinite(Number(cessTotal))
        ? 0
        : Number(cessTotal);

    const calculatedLineTotal =
      safeAmountForTotal +
      safeIgstAmount +
      safeCgstAmount +
      safeSgstAmount +
      safeCessTotal;

    // Final safety check to ensure lineTotal is never NaN
    item.lineTotal =
      isNaN(calculatedLineTotal) || !isFinite(calculatedLineTotal)
        ? 0
        : calculatedLineTotal;
    const materialLabel = materialOptions.find(
      (item) => item.value == materialName
    )?.label;
    const uomLabel = uomOptions.find((item) => item.value == uomId)?.label;
    const selectedMakeIds = Array.isArray(make) ? make : [];
    const makeInfoData = selectedMakeIds
      .map((id: any) => ({
        makeId: Number(id),
        makeName: makeOptions.find((item) => item.value == id)?.label || "",
      }))
      .filter((m: any) => m.makeId && m.makeName);
    // Duplicate validation
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    const editIndex =
      formData.action === "edit" && formData.index != null
        ? formData.index
        : -1;

    const isDuplicate = existingItems.some((item: any, idx: number) => {
      if (idx === editIndex) return false;
      return (
        item.materialName == materialName &&
        item.materialCategory == materialCategory
      );
    });
    if (isDuplicate) {
      setAlertInfo({
        type: "error",
        message: "This material already exists for the selected category.",
        errors: [],
      });
      return;
    }

    if (formData.action == "add" || formData.index == null) {
      append({
        key: nanoid(),
        materialCategory,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        unitPrice,
        uomId,
        uomLabel,
        makeInfo: makeInfoData,
        amount: (isNaN(item.amount) || !isFinite(item.amount)
          ? 0
          : item.amount
        ).toString(),
        description,
        igst: item.igst.toString(),
        igstPercentage: (isNaN(item.igstPercentage) ||
          !isFinite(item.igstPercentage)
          ? 0
          : item.igstPercentage
        ).toString(),
        igstAmount: (isNaN(item.igstAmount) || !isFinite(item.igstAmount)
          ? 0
          : item.igstAmount
        ).toString(),
        cgst: item.cgst.toString(),
        cgstPercentage: (isNaN(item.cgstPercentage) ||
          !isFinite(item.cgstPercentage)
          ? 0
          : item.cgstPercentage
        ).toString(),
        cgstAmount: (isNaN(item.cgstAmount) || !isFinite(item.cgstAmount)
          ? 0
          : item.cgstAmount
        ).toString(),
        sgst: item.sgst.toString(),
        sgstPercentage: (isNaN(item.sgstPercentage) ||
          !isFinite(item.sgstPercentage)
          ? 0
          : item.sgstPercentage
        ).toString(),
        sgstAmount: (isNaN(item.sgstAmount) || !isFinite(item.sgstAmount)
          ? 0
          : item.sgstAmount
        ).toString(),
        cess: _cess,
        lineTotal: (isNaN(item.lineTotal) || !isFinite(item.lineTotal)
          ? 0
          : item.lineTotal
        ).toString(),
        needByDate,
        attachment: attachment?.length ? true : false,
        file: attachment,
        deliveryAddressId,
      });
    } else {
      update(formData.index, {
        ...formData.item,
        key: nanoid(),
        materialCategory,
        materialName,
        materialLabel,
        hsnCode,
        quantity,
        unitPrice,
        uomId,
        uomLabel,
        makeInfo: makeInfoData,
        amount: (isNaN(item.amount) || !isFinite(item.amount)
          ? 0
          : item.amount
        ).toString(),
        description,
        igst: item.igst.toString(),
        igstPercentage: (isNaN(item.igstPercentage) ||
          !isFinite(item.igstPercentage)
          ? 0
          : item.igstPercentage
        ).toString(),
        igstAmount: (isNaN(item.igstAmount) || !isFinite(item.igstAmount)
          ? 0
          : item.igstAmount
        ).toString(),
        cgst: item.cgst.toString(),
        cgstPercentage: (isNaN(item.cgstPercentage) ||
          !isFinite(item.cgstPercentage)
          ? 0
          : item.cgstPercentage
        ).toString(),
        cgstAmount: (isNaN(item.cgstAmount) || !isFinite(item.cgstAmount)
          ? 0
          : item.cgstAmount
        ).toString(),
        sgst: item.sgst.toString(),
        sgstPercentage: (isNaN(item.sgstPercentage) ||
          !isFinite(item.sgstPercentage)
          ? 0
          : item.sgstPercentage
        ).toString(),
        sgstAmount: (isNaN(item.sgstAmount) || !isFinite(item.sgstAmount)
          ? 0
          : item.sgstAmount
        ).toString(),
        cess: _cess,
        lineTotal: item.lineTotal.toString(),
        needByDate,
        attachment: attachment?.length ? true : false,
        file: attachment,
        deliveryAddressId,
        dms: dmsFile ? formData?.item?.dms : [],
      });
    }
    closeModal();
  };
  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }
    let address = options?.find((item: any) => item[matchKey] == id);
    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city}, {address?.stateName}, {address?.countryName} -{" "}
              {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };
  useEffect(() => {
    fetchDeliveryAddress();
  }, []);

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mt-12">
              <div className="col-md-4">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptionsWithRC}
                  placeholder="Select Material Category"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId)
                      ? true
                      : false
                  }
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                    setMakeOptions([]);
                    setValue("make", []);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions.filter(
                    (option) => !selectedMaterials.includes(option.value)
                  )}
                  placeholder="Select Material Name"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId)
                      ? true
                      : false
                  }
                  onChange={async (e: any) => {
                    setValue("make", []);
                    await Promise.all([
                      fetchUom(e, materialData, null),
                      fetchMakeOptions(e),
                    ]);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  type="number"
                  // required={true}
                  name="hsnCode"
                  label="HSN/SAC"
                  pattern={/^$|^[0-9]{4,8}$/}
                  placeholder="Enter HSN"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      // Allow editing if HSN is missing/empty, even if from Rate Contract
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId &&
                        formData.item?.hsnCode)
                      ? true
                      : false
                  }
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-4">
                <FormMultiSelect
                  name="make"
                  label="Make"
                  options={makeOptions}
                  placeholder="Select Make"
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-2">
                <FormInput
                  type="number"
                  min={0.1}
                  max={formData.item?.prMaxQuantity}
                  required={true}
                  name="quantity"
                  label="Quantity"
                  placeholder="Enter Quantity"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId)
                      ? true
                      : false
                  }
                  customValidation={
                    formData.item?.prMaxQuantity != null
                      ? {
                          validate: (value: any) => {
                            const max = Number(formData.item.prMaxQuantity);
                            return Number(value) <= max ||
                              `Quantity cannot exceed PR quantity (${max})`;
                          },
                        }
                      : undefined
                  }
                />
              </div>
              <div className="col-md-2">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId)
                      ? true
                      : false
                  }
                />
              </div>
              <div className="col-md-4">
                <FormInput
                  type="number"
                  pattern={/^\d+(\.\d+)?$/}
                  required={true}
                  min={1}
                  name="unitPrice"
                  label="Price per Unit"
                  placeholder="Enter Price per Unit"
                  disabled={
                    (quotationId && formData.item?.quoteShortListId) ||
                      (convertedFrom === "RC" &&
                        formData.item?.rateContractLineId)
                      ? true
                      : false
                  }
                />
              </div>
              {taxConfiguration?.gstType == "IGST" ? (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="igst"
                      label="IGST(%)"
                      required={true}
                      options={taxDropdownOptions}
                      placeholder="Enter IGST"
                      disabled={
                        (quotationId && formData.item?.quoteShortListId) ||
                          (convertedFrom === "RC" &&
                            formData.item?.rateContractLineId)
                          ? true
                          : false
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="cgst"
                      label="CGST/SGST(%)"
                      required={true}
                      options={taxDropdownOptions}
                      placeholder="Enter CGST"
                      disabled={
                        (quotationId && formData.item?.quoteShortListId) ||
                          (convertedFrom === "RC" &&
                            formData.item?.rateContractLineId)
                          ? true
                          : false
                      }
                    />
                  </div>
                  <div style={{ marginTop: "15px" }}></div>
                </>
              )}
              {taxConfiguration?.cess?.map((item: any, index: any) => {
                if (!item.cessName) return null;
                return (
                  <>
                    {index === 0 && taxConfiguration?.gstType === "IGST" && (
                      <div style={{ marginTop: "15px" }}></div>
                    )}
                    <div className="col-md-4">
                      <FormInput
                        type="number"
                        required={true}
                        name={item.cessName}
                        label={item.cessName + "(%)"}
                        placeholder={`Enter ${item.cessName}`}
                        disabled={
                          (quotationId && formData.item?.quoteShortListId) ||
                            (convertedFrom === "RC" &&
                              formData.item?.rateContractLineId)
                            ? true
                            : false
                        }
                      />
                    </div>
                  </>
                );
              })}
              {needByDateType === "Line Level" && (
                <div className="col-md-4">
                  <FormDatePicker
                    name="needByDate"
                    label="Need By Date"
                    required={true}
                    minDate={new Date()}
                    disabled={
                      quotationId && formData.item?.quoteShortListId
                        ? true
                        : false
                    }
                  />
                </div>
              )}
              <Col md={4}>
                {((files && files[0]?.name) ||
                  dmsFile ||
                  dmsFile?.fileName) && (
                    <div className="form-group m-0">
                      <div className="form-label-wrapper">
                        <label className="form-label" htmlFor={"attachment"}>
                          Attachment (If any)
                        </label>
                        <TooltipComponent
                          iconClass="card-hint"
                          icon="help-fill"
                          direction="left"
                          id={`file-tooltip`}
                          text="e.g: Design specificaitons"
                          containerClassName={""}
                        />
                      </div>
                      <div
                        className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                        style={{ borderRadius: "3px" }}
                      >
                        <span>
                          {(files && files[0]?.name) || dmsFile?.fileName}
                        </span>
                        <span
                          onClick={() => {
                            setValue("attachment", null);
                            setDmsFile(null);
                          }}
                          style={{
                            cursor: "pointer",
                            marginLeft: "10px",
                            display: "block",
                            fontSize: 14,
                          }}
                        >
                          <Icon name="cross"></Icon> &nbsp;
                        </span>
                      </div>
                    </div>
                  )}
                <div
                  style={{
                    display: `${(files && files[0]?.name) || dmsFile?.fileName
                        ? "none"
                        : "block"
                      }`,
                  }}
                >
                  <FormInput
                    type="file"
                    name="attachment"
                    label="Attachment (If any) "
                    placeholder="Enter Attachment"
                    tooltipText="e.g: Design specificaitons"
                    disabled={
                      quotationId && formData.item?.quoteShortListId
                        ? true
                        : false
                    }
                  />
                </div>
              </Col>

              {deliveryAddressType === "Line Level" && (
                <>
                  <div className="col-md-4">
                    <FormSelect
                      name="deliveryAddressId"
                      label="Delivery Address"
                      required={true}
                      options={deliveryAddressOptions}
                      popOverOptions={
                        <ShowAddressPopOver
                          id={deliveryAddressId}
                          matchKey="id"
                          options={deliveryAddressList}
                          text="Address"
                        />
                      }
                      placeholder="Select Delivery Address"
                      tooltipText="Select the delivery address for the purchase order."
                      disabled={
                        quotationId && formData.item?.quoteShortListId
                          ? true
                          : false
                      }
                    />
                  </div>
                </>
              )}
            </Row>
            {/* Site hierarchy fields removed */}
            <Row className="mt-4">
              <div className="col-md-12">
                <FormTextArea
                  name="description"
                  label="Description (if any)"
                  placeholder="Enter Description"
                  minLength={5}
                  maxLength={500}
                  disabled={
                    quotationId && formData.item?.quoteShortListId
                      ? true
                      : false
                  }
                />
              </div>
            </Row>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {formData.action === "edit" && formData.index != null && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Update
                      </Button>
                    </li>
                  )}
                  {(formData.action == "add" || formData.index == null) && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
